import { useMemo } from 'react';
import {
    EnquiryAudienceEnum,
    type EnquiryFormPageType,
    type EnquiryFormTrigger,
} from 'generated/graphql';
import { useUser } from '~/modules/user/hook/use-user';
import {
    getEnquiryVisibility,
    type EnquiryVisibilityResult,
} from '../util/get-enquiry-visibility';
import type { UseEnquiryProps } from './use-enquiry';
import { useEnquiryFormForPage } from './use-enquiry-form-for-page';

export interface UseEnquiryVisibilityOptions {
    /** Which Forms page-type cell gates this CTA. Defaults to the current route, then `itemType`. */
    pageType?: EnquiryFormPageType;
    itemType?: UseEnquiryProps['itemType'];
    /** Which CTA on that page type. Defaults to Enquire. */
    trigger?: EnquiryFormTrigger;
    /** Product pages: the product shown, whose own choice for the button wins. */
    productId?: number | null;
    /**
     * Ask for the Forms assignment. Defaults to true; false keeps the flat
     * field until the caller knows the visitor is interested.
     */
    enabled?: boolean;
}

export interface EnquiryVisibility extends EnquiryVisibilityResult {
    /** The Forms assignment was asked for and has not answered yet. */
    isAssignmentLoading: boolean;
}

/**
 * Hook to determine the visibility of enquiry CTAs and forms based on the
 * professional's enquiry audience setting and the current user's classification.
 *
 * With Business Manager Forms on, the professional's per-page-type assignment
 * takes over: a switched-off cell hides everything, an assigned form supplies
 * the audience. Until that answer lands (and whenever the flag is off) the flat
 * `EnquiryAudience` field decides, so nothing flashes on for professionals who
 * have enquiries off in both places.
 *
 * @param professional - The professional's enquiry settings. `ID` enables the
 *   Forms lookup; callers without it keep the flat-field behaviour.
 * @param options - Which page-type / trigger cell to consult. The page type
 *   defaults to the public route the visitor is on (`/project/…`, …).
 * @returns The visibility result for enquiry elements, and whether the Forms
 *   assignment is still on its way.
 *
 * @example
 * ```tsx
 * const { showEnquiryCTA, showEnquiryForm, showSpecifierLoginPrompt } =
 *     useEnquiryVisibility(product.Professional);
 *
 * return (
 *     <>
 *         {showEnquiryCTA && <EnquiryButton />}
 *         {showEnquiryForm && <EnquiryForm />}
 *         {showSpecifierLoginPrompt && <SpecifierLoginPrompt />}
 *     </>
 * );
 * ```
 */
export function useEnquiryVisibility(
    {
        EnquiryAudience,
        ID,
    }: {
        /** The professional's enquiry audience setting. */
        EnquiryAudience: EnquiryAudienceEnum;
        /** Needed to look up the professional's Forms assignments. */
        ID?: number | string | null;
    },
    {
        pageType,
        itemType,
        trigger,
        productId,
        enabled = true,
    }: UseEnquiryVisibilityOptions = {},
): EnquiryVisibility {
    const user = useUser();
    const professionalId = ID ? Number(ID) : null;

    const assignment = useEnquiryFormForPage({
        professionalId,
        pageType,
        itemType,
        trigger,
        productId,
        enabled,
    });

    const enquiryAudience =
        assignment.status === 'off'
            ? EnquiryAudienceEnum.NoEnquiries
            : (assignment.form?.audience ?? EnquiryAudience);

    const isAssignmentLoading = assignment.isLoading;
    return useMemo(
        () => ({
            ...getEnquiryVisibility({ enquiryAudience, user }),
            isAssignmentLoading,
        }),
        [enquiryAudience, user, isAssignmentLoading],
    );
}
